import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { ArticlePlacementHandler } from 'app/modules/wallforming/components/scenes/objects/articles/ArticlePlacementHandler';
import { GLTFLoader } from 'app/modules/wallforming/components/scenes/objects/helpers/GLTFLoader';
import { RepresentationLoader } from 'app/modules/wallforming/components/scenes/objects/helpers/RepresentationLoader';
import { QuickPanelReplacementPreview } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/QuickPanelReplacementPreview';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { toastNotifications } from 'shared/utils/toastNotifications';

interface ArticleDataHandlerProps {
    articleId: string;
    isMainCanvas: boolean;
}

/**
 * Responsibilities:
 * - Loads and manages article data and its associated assets for a given articleId.
 * - Handles error boundaries and displays notifications on data loading failures.
 * - Coordinates loading of representations and GLTF assets for articles.
 * - Provides loaded data to child components for further processing and rendering.
 * - Ensures proper cleanup of article instances on error.
 */
export const ArticleDataHandler = ({ articleId, isMainCanvas }: ArticleDataHandlerProps) => {
    const { t } = useTranslation();

    const removeAllArticleInstances = usePlacementStore(useShallow((state) => state.removeAllArticleInstances));

    const loadArticleData = usePlacementStore(
        useShallow((state) => Object.keys(state.articles[articleId]?.articleInstances ?? {}).length > 0)
    );

    if (!loadArticleData) {
        return null;
    }

    return (
        <ErrorBoundary
            key={location.pathname}
            fallbackRender={() => null}
            onError={() => {
                toastNotifications.error({
                    title: t('error'),
                    message: t('errorInsertArticle')
                });
                removeAllArticleInstances(articleId, true);
            }}
        >
            <Suspense fallback={<QuickPanelReplacementPreview articleId={articleId} />}>
                <RepresentationLoader articleId={articleId} isInteractive={isMainCanvas}>
                    {({ articleId, articleRepresentation, gltfAssets }) => (
                        <GLTFLoader
                            articleId={articleId}
                            articleRepresentation={articleRepresentation}
                            gltfAssets={gltfAssets}
                            isMainCanvas={isMainCanvas}
                        >
                            {({ articleId, articleMeshes, isMainCanvas }) => (
                                <ArticlePlacementHandler
                                    articleId={articleId}
                                    articleMeshes={articleMeshes}
                                    articleRepresentation={articleRepresentation}
                                    isMainCanvas={isMainCanvas}
                                />
                            )}
                        </GLTFLoader>
                    )}
                </RepresentationLoader>
            </Suspense>
        </ErrorBoundary>
    );
};
